import { normalizeCampaignContentSlot } from "@runcheetah/shared";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type CampaignGetScheduledPost = RouterOutputs["campaign"]["get"]["scheduledPosts"][number];
type CampaignListScheduledPost =
  RouterOutputs["campaign"]["list"][number]["scheduledPosts"][number];
type CampaignScheduledPost = CampaignGetScheduledPost | CampaignListScheduledPost;
type StandaloneScheduledPost =
  RouterOutputs["standaloneAssetBatch"]["get"]["scheduledPosts"][number];

type ScheduledPostWithActiveImageRegenerations = {
  id: string;
  activeImageRegenerationBlockIds?: string[];
};

export const hasActiveImageRegenerationForPost = (
  scheduledPosts: ScheduledPostWithActiveImageRegenerations[] | undefined,
  scheduledPostId: string | undefined,
): boolean =>
  Boolean(
    scheduledPostId &&
    scheduledPosts?.some(
      (post) =>
        post.id === scheduledPostId && (post.activeImageRegenerationBlockIds?.length ?? 0) > 0,
    ),
  );

export const normalizeCampaignScheduledPosts = (
  scheduledPosts: CampaignScheduledPost[] | undefined,
): CampaignSlotCardData[] =>
  (scheduledPosts ?? []).map((post, index) => ({
    ...post,
    id: post.id,
    scheduledFor: post.scheduledFor,
    status: post.status,
    approvalStatus: post.approvalStatus as CampaignSlotCardData["approvalStatus"],
    isPreview: index < 1,
    draft: ("draft" in post ? post.draft : null) as CampaignSlotCardData["draft"],
    draftUpdatedAt: ("draftUpdatedAt" in post
      ? post.draftUpdatedAt
      : null) as CampaignSlotCardData["draftUpdatedAt"],
    publishedDraft: ("publishedDraft" in post
      ? post.publishedDraft
      : null) as CampaignSlotCardData["publishedDraft"],
    publishedAt: ("publishedAt" in post
      ? post.publishedAt
      : null) as CampaignSlotCardData["publishedAt"],
    hasUnpublishedDraft: "hasUnpublishedDraft" in post ? post.hasUnpublishedDraft : false,
    activeImageRegenerationBlockIds:
      "activeImageRegenerationBlockIds" in post ? post.activeImageRegenerationBlockIds : [],
    slot: normalizeCampaignContentSlot({
      slotJson: ("slot" in post ? post.slot : undefined) ?? undefined,
    }),
    output:
      "output" in post && post.output
        ? (post.output as CampaignSlotCardData["output"])
        : { latestAttempt: null, latestCompleted: null, latestCopy: null },
  }));

export const normalizeStandaloneScheduledPosts = (
  scheduledPosts: StandaloneScheduledPost[] | undefined,
): CampaignSlotCardData[] =>
  (scheduledPosts ?? []).map((post, index) => ({
    id: post.id,
    scheduledFor: post.scheduledFor,
    status: post.status,
    approvalStatus: post.approvalStatus as CampaignSlotCardData["approvalStatus"],
    isPreview: index < 2,
    draft: post.draft,
    draftUpdatedAt: post.draftUpdatedAt,
    publishedDraft: post.publishedDraft,
    publishedAt: post.publishedAt,
    hasUnpublishedDraft: post.hasUnpublishedDraft,
    activeImageRegenerationBlockIds: post.activeImageRegenerationBlockIds,
    publication: "publication" in post ? post.publication : null,
    instagramConnection: "instagramConnection" in post ? post.instagramConnection : null,
    slot: normalizeCampaignContentSlot({ slotJson: post.slot }),
    output: post.output
      ? (post.output as CampaignSlotCardData["output"])
      : {
          latestAttempt: null,
          latestCompleted: null,
          latestCopy: null,
        },
  }));
